import { createAction, createAsyncThunk, Selector } from "@reduxjs/toolkit"
import { LearnersPreferences, Preference, Priority, RouteState } from "./types"
import { subjectSelectors } from "./subjects-slice"
import { preferencesSelectors } from "./preferences-slice"
import { prioritiesSelectors } from "./priorities-slice"

type SelectorStateInterface<InputSelector> =
  InputSelector extends Selector<infer State> ? State : never

type RootState = SelectorStateInterface<
  typeof subjectSelectors.selectSubjects
> &
  SelectorStateInterface<typeof preferencesSelectors.selectPreference> &
  SelectorStateInterface<typeof prioritiesSelectors.selectPriority>

export const createSyncPreferencesThunk = <T>(name: string) =>
  createAsyncThunk<
    undefined,
    T,
    { state: RootState; extra: { route: RouteState } }
  >(name, async (_, { getState, extra, rejectWithValue }) => {
    const state = getState()
    const { route } = extra

    const token = document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] || ""
    return fetch(route.href, {
      method: "PUT",
      body: JSON.stringify({
        ...route.data,
        preferences: {
          student_preference_set: subjectSelectors
            .selectSubjects(state)
            .map((subject) => ({
              subject: subject.id,
              preferences: preferencesSelectors
                .selectSubjectPreferences(state, { subjectId: subject.id })
                .map((preference) => ({
                  id: preference.id,
                  rank: preference.rank,
                  preference_priorities_attributes: preference.priorities.map(
                    (id) => ({
                      priority: id,
                    }),
                  ),
                })),
            })),
        },
      }),
      headers: {
        "X-XSRF-TOKEN": token,
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) return rejectWithValue(response.status)
      })
      .catch((err) => rejectWithValue(err))
  })

export const removePreference = createSyncPreferencesThunk<{
  preference: Preference
}>("preferences/removePreference")
export const associatePriorityWithPreference = createSyncPreferencesThunk<{
  priority: Priority
  preference: Preference
}>("preferences/associatePriorityWithPreference")
export const dissociatePriorityWithPreference = createSyncPreferencesThunk<{
  priority: Priority
}>("preferences/dissociatePriorityWithPreference")
export const promotePreferenceRank = createSyncPreferencesThunk<{
  preference: Preference
}>("preferences/promotePreferenceRank")
export const demotePreferenceRank = createSyncPreferencesThunk<{
  preference: Preference
}>("preferences/demotePreferenceRank")

export const dataLoaded = createAction<LearnersPreferences[]>(
  "preferences/dataLoaded",
)
